import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {Switch, Route, withRouter} from 'react-router-dom';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import TimeAgo from '../../components/TimeAgo';
import Content from '../../components/Content';
import Loading from '../../components/Loading';
import SideModal from '../../components/SideModal';
import AuditLog, {optimisticLogConfirmed} from '../../components/AuditLog';
import Answer from '../../components/Answer';
import Nl2br from '../../components/Nl2br';
import VirtualizedGrid from '../../components/VirtualizedGrid';
import Icon from '../../components/Icon';
import Tabs from '../../components/Tabs';
import Form from '../../components/Form';
import Button from '../../components/Button';
import TextArea from '../../components/TextArea';
import HoverTooltip from '../../components/HoverTooltip';
import PrintToZebraControl from '../../components/PrintToZebraControl';
import * as flags from '../../lib/flags';
import CanReviewTemplateData from '../../security/rules/CanReviewTemplateData';
import CanSecondLevelReviewTemplateData from '../../security/rules/CanSecondLevelReviewTemplateData';
import CanEditUser from '../../security/rules/CanEditUser';

import AnswersTab from './AnswersTab';
import {calculateAlerts} from './shared';
import Role from "../../lib/Role";
import {isQuestionDeleted, getQuestionDisplayLabel} from "../FormBuilderScreen/shared";

class ViewDataTab extends PureComponent {
  state = {
    loading: true,
    reviewComment: '',
    submittingReview: false,
    submittingComment: false,
    editingCommentRowId: null,
    optimisticLogsByRow: {}
  };

  async componentDidMount() {
    const {template, sheets, rows} = this.props;
    const worker = new Worker('/workers/view-data-tab-worker.js');
    const questionsById = [...template.contextQuestions, ...template.questions]
      .reduce((map, q) => { map[q.id] = q; return map; }, {});
    worker.onmessage = e => {
      const data = e.data.columns
        ? {...e.data, columns: e.data.columns.map(col => questionsById[col.key]
            ? {...col, title: getQuestionDisplayLabel(questionsById[col.key])}
            : col)}
        : e.data;
      this.setState(data);
    };
    worker.postMessage([template, sheets, rows]);
    this.worker = worker;
  }

  handleUpdateAnswers = async (sheet, row, question, answers) => {
    const {onAnswersChange, user} = this.props;
    let {columns, data, rows, rowsById, sheetsById} = this.state;

    // Need to update the copy of our own state as if it was built by the web worker.
    // Update the data in the grid. First, figure out what column the data is stored, then update the appropriate row.
    const columnIndex = columns.map(column => column.key).indexOf(question.id);

    if (columnIndex >= 0) {
      data = data.map(gridRow => {
        const cell = gridRow[columnIndex];

        if (cell.rowId === row.id || (question.context && cell.sheetId === sheet.id)) {
          const copy = [...gridRow];
          copy[columnIndex] = {...copy[columnIndex], answers};

          return copy;
        }

        return gridRow;
      });
    }

    if (question.context) {
      // If the question was a context question, we need to update the contextAnswers property on the correct sheet
      // object.
      sheetsById = {
        ...sheetsById,
        [sheet.id]: {
          ...sheetsById[sheet.id],
          contextAnswers: sheetsById[sheet.id].contextAnswers
            .filter(answer => answer.questionId !== question.id)
            .concat(answers)
        }
      };
    }
    else {
      // If the question was not a context question, then we need to update the rows. Iterate over the rows and update
      // the answers, then rebuild the rowsById map.
      rows = rows.map(prevRow => {
        if (prevRow.id === row.id || (question.context && prevRow.sheetId === sheet.id)) {
          return {
            ...prevRow,
            answers: prevRow.answers
              .filter(answer => answer.questionId !== question.id)
              .concat(answers),
            // Mark row as edited and change review state to pending if it was approved
            edited: true,
            lastEditedBy: user,
            reviewState: 'PENDING',
          };
        }
        return prevRow;
      });

      rowsById = rows.reduce((map, row) => {
        map[row.id] = row;
        return map;
      }, {});
    }

    // Update the data grid to reflect the new row state for the DATE and REVIEW_STATUS columns
    const dateColumnIndex = columns.findIndex(col => col.key === 'last-modified-date');
    const reviewStatusColumnIndex = columns.findIndex(col => col.key === 'review-status');

    if (dateColumnIndex >= 0 || reviewStatusColumnIndex >= 0) {
      data = data.map(gridRow => {
        // Find the row that matches our updated row
        const answerCell = gridRow.find(cell => cell.type === 'ANSWER' && cell.rowId === row.id);
        if (answerCell) {
          const copy = [...gridRow];
          const updatedRow = rows.find(r => r.id === row.id);

          // Update the DATE cell to show the edit icon
          if (dateColumnIndex >= 0 && updatedRow) {
            copy[dateColumnIndex] = {
              ...copy[dateColumnIndex],
              isEdited: updatedRow.edited,
              editedBy: updatedRow.lastEditedBy
            };
          }

          // Update the REVIEW_STATUS cell
          if (reviewStatusColumnIndex >= 0 && updatedRow) {
            copy[reviewStatusColumnIndex] = {
              ...copy[reviewStatusColumnIndex],
              row: updatedRow
            };
          }

          return copy;
        }
        return gridRow;
      });
    }

    this.setState({data, rows, rowsById, sheetsById});

    // Notify the parent that the answers have changed.
    onAnswersChange(sheet, row, question, answers);
  }

  handleCellClick = (cell, rowIndex) => {
    const {user, history, match: {params: {sheetIds, templateId}}} = this.props;
    const {rows} = this.state;
    const row = rows[rowIndex];

    if (cell.type === 'SHEET_LINK') {
      history.push(`/sheet/${cell.sheet.template.id}/${cell.sheet.id}`);
    }
    else if (cell.type === 'USER_LINK' && CanEditUser(user, cell.user)) {
      history.push(`/users/${cell.user.id}`);
    }
    else {
      history.push(`/sheet/${templateId}/${sheetIds}/data/${row.id}`);
    }
  }

  handleCloseRowDetails = () => {
    const {history, match: {params: {sheetIds, templateId}}} = this.props;
    history.push(`/sheet/${templateId}/${sheetIds}/data`);
  }

  handleReviewRow = async (row, state) => {
    const {credentials, onRowChange} = this.props;
    const {sheetsById, reviewComment} = this.state;
    let response;

    try {
      this.setState({submittingReview: true});
      response = await api.reviewSheetRow(row.sheetId, row.id, state, reviewComment, credentials);
    }
    catch (e) {
      this.setState({submittingReview: false});
      NotificationManager.error('There was an error while submitting your review. Please try again.');
      return;
    }

    const nextRow = {...row, ...response};

    // Update the view data tab state to keep it consistent.
    this.setState(prevState => {
      const reviewStatusIndex = prevState.data[0].map(cell => cell.type).indexOf('REVIEW_STATUS');

      return {
        submittingReview: false,
        reviewComment: '',
        rows: prevState.rows.map(row => row.id === nextRow.id ? nextRow : row),
        rowsById: {
          ...prevState.rowsById,
          [nextRow.id]: nextRow
        },
        data: prevState.data.map(gridRow => {
          if (gridRow[reviewStatusIndex].row.id === nextRow.id) {
            // Found the data row that contains the data for this sheet row.
            const copy = [...gridRow];
            copy[reviewStatusIndex] = {...copy[reviewStatusIndex], row: nextRow};
            return copy;
          }

          return gridRow;
        })
      };
    });

    // Notify the parent that the row has changed.
    onRowChange(sheetsById[row.sheetId], response);
  }

  handleAddRowComment = async (row, comment) => {
    const {credentials, onRowChange, user} = this.props;
    const {sheetsById} = this.state;
    const previousComment = row.overallComments;
    let response;

    try {
      this.setState({submittingComment: true});
      response = await api.addRowComment(row.sheetId, row.id, comment, credentials);
    }
    catch (e) {
      this.setState({submittingComment: false});
      NotificationManager.error('There was an error while saving your comment. Please try again.');
      return;
    }

    const nextRow = {...row, ...response};

    //build an optimistic audit-log entry so the History tab reflects the new comment immediately. 
    const optimisticLog = {
      id: `optimistic-comment-${row.id}-${Date.now()}`,
      user: {...user, type: user.type || 'USER'},
      timestamp: new Date().toISOString(),
      action: 'overall_comment_changed',
      objectKey: row.id,
      comment: null,
      previous: previousComment && previousComment.trim() ? [previousComment] : [],
      next: [comment]
    };

    //also update the view data tab state to keep it consistent so the footer reflects the saved comment, and
    //collapse the editor back to the trigger button now that the comment is saved
    this.setState(prevState => {
      //update the grid's date cell so the "has comment" badge appears/updates without rebuilding the whole grid.
      const dateColumnIndex = prevState.columns.findIndex(col => col.key === 'last-modified-date');
      const hasComment = !!(nextRow.overallComments && nextRow.overallComments.trim());
      const data = dateColumnIndex < 0 ? prevState.data : prevState.data.map(gridRow => {
        const answerCell = gridRow.find(cell => cell.type === 'ANSWER' && cell.rowId === row.id);
        if (!answerCell) {
          return gridRow;
        }
        const copy = [...gridRow];
        copy[dateColumnIndex] = {...copy[dateColumnIndex], hasComment};
        return copy;
      });

      return {
        submittingComment: false,
        editingCommentRowId: null,
        data,
        rows: prevState.rows.map(r => r.id === nextRow.id ? nextRow : r),
        rowsById: {
          ...prevState.rowsById,
          [nextRow.id]: nextRow
        },
        optimisticLogsByRow: {
          ...prevState.optimisticLogsByRow,
          [row.id]: [optimisticLog, ...(prevState.optimisticLogsByRow[row.id] || [])]
        }
      };
    });

    NotificationManager.success('Your comment was saved successfully');

    //notify the parent that the row has changed.
    onRowChange(sheetsById[row.sheetId], nextRow);
  }

  clearOptimisticCommentLogs = (rowId, loadedLogs = []) => {
    this.setState(prevState => {
      const existing = prevState.optimisticLogsByRow[rowId];
      if (!existing) {
        //there is nothing to clear- return null so React skips a needless re render
        return null;
      }

      //only drop the optimistic entries the real feed has caught up to; keep any whose async audit write is still
      //in flight so the change doesn't disappear before the server confirms it
      const remaining = existing.filter(optimistic => !optimisticLogConfirmed(optimistic, loadedLogs));
      if (remaining.length === existing.length) {
        //nothing matched yet- skip a needless re render
        return null;
      }

      const optimisticLogsByRow = {...prevState.optimisticLogsByRow};
      if (remaining.length > 0) {
        optimisticLogsByRow[rowId] = remaining;
      }
      else {
        delete optimisticLogsByRow[rowId];
      }
      return {optimisticLogsByRow};
    });
  }

  handleDeleteRow = async row => {
    const {onRowDeleted, credentials} = this.props;
    const {sheetsById} = this.state;

    // eslint-disable-next-line
    if (!confirm('Are you sure you want to permanently delete this row? This action cannot be undone!')) {
      return;
    }

    try {
      await api.deleteSheetRow(row.sheetId, row.id, credentials);
      NotificationManager.success('This row was deleted successfully');
      this.handleCloseRowDetails();

      // Update the view data tab state to keep it consistent.
      this.setState(prevState => {
        return {
          rows: prevState.rows.filter(r => r.id !== row.id),
          rowsById: Object.keys(prevState.rowsById).filter(key => key !== row.id).reduce((rowsById, rowId) => {
            rowsById[rowId] = prevState.rowsById[rowId];
            return rowsById;
          }, {}),
          data: prevState.data.filter(dataRow => {
            const anyAnswerColumn = dataRow.filter(cell => cell.type === 'ANSWER')[0];
            console.log(row, anyAnswerColumn);
            return anyAnswerColumn && anyAnswerColumn.rowId !== row.id;
          })
        };
      });

      // Notify the parent component that the row was deleted.
      onRowDeleted(sheetsById[row.sheetId], row);
    }
    catch (e) {
      NotificationManager.error('An error occurred while deleting this row. Please try again.');
    }
  }

  renderRowFooter = row => {
    const {user, template, credentials} = this.props;
    const {reviewComment, submittingReview, submittingComment} = this.state;

    const hasReviewers = template.reviewers.length > 0;
    const canFirstLevel = CanReviewTemplateData(user, template);
    const canSecondLevel = CanSecondLevelReviewTemplateData(user, template);
    const userCastFirstLevelReview = row.reviewer && row.reviewer.id === user.id;
    const showFirstLevelActions = hasReviewers && canFirstLevel && row.reviewState === 'PENDING';
    const showSecondLevelActions = hasReviewers && template.secondLevelReviewEnabled && canSecondLevel && row.reviewState === 'IN_REVIEW' && !userCastFirstLevelReview;
    const showAwaitingSecondLevel = hasReviewers && template.secondLevelReviewEnabled && row.reviewState === 'IN_REVIEW' && !showSecondLevelActions;
    const showAwaitingFirstLevel = hasReviewers && template.secondLevelReviewEnabled && canSecondLevel && !canFirstLevel && row.reviewState === 'PENDING';
    const showReviewComment = showFirstLevelActions || showSecondLevelActions;
    const isEditingComment = this.state.editingCommentRowId === row.id;
    const hasOverallComment = !!(row.overallComments && row.overallComments.trim());

    return (
      <SheetRowFooter>
        <Form>
          {canFirstLevel && (isEditingComment ? (
            <RowCommentEditor
              key={row.id}
              row={row}
              submitting={submittingComment}
              onSave={comment => this.handleAddRowComment(row, comment)}
              onCancel={() => this.setState({editingCommentRowId: null})}
            />
          ) : hasOverallComment && (
            <OverallCommentContainer>
              <FooterFieldLabel>Overall comment</FooterFieldLabel>
              <SavedComment><Nl2br>{row.overallComments}</Nl2br></SavedComment>
            </OverallCommentContainer>
          ))}
          {showReviewComment && (
            <ReviewCommentContainer>
              <FooterFieldLabel>Review comment</FooterFieldLabel>
              <ReviewCommentTextArea
                disabled={submittingReview}
                placeholder="Write a review comment (optional)"
                defaultValue={reviewComment}
                onBlur={e => this.setState({reviewComment: e.target.value})}
              />
            </ReviewCommentContainer>
          )}
          <SheetRowFooterInfo>
            <ReviewStateContainer>
              {hasReviewers && (
                <>
                  {(showFirstLevelActions || showSecondLevelActions) ? (
                    <>
                      <Button
                        secondary
                        disabled={submittingReview}
                        type="button"
                        onClick={() => this.handleReviewRow(row, 'APPROVED')}>
                        <Icon name="check" /> Approve
                      </Button>
                      <Button
                        secondary
                        disabled={submittingReview}
                        type="button"
                        onClick={() => this.handleReviewRow(row, 'REJECTED')}>
                        <Icon name="slash" /> Reject
                      </Button>
                    </>
                  ) : showAwaitingSecondLevel ? (
                    <ReviewState inverse state="IN_REVIEW">Awaiting second level review</ReviewState>
                  ) : showAwaitingFirstLevel ? (
                    <ReviewState inverse state="PENDING">Awaiting first level review</ReviewState>
                  ) : (
                    <>
                      <ReviewState inverse state={row.reviewState}>{formatReviewState(row.reviewState)}</ReviewState>
                      {template.secondLevelReviewEnabled ? (
                        <>
                          {row.reviewer && <>&nbsp;by&nbsp;<strong>{row.reviewer.userName}</strong>&nbsp;<TimeAgo date={row.reviewDate} /></>}
                          {row.secondLevelReviewer && <>&nbsp;and&nbsp;<strong>{row.secondLevelReviewer.userName}</strong>&nbsp;<TimeAgo date={row.secondLevelReviewDate} /></>}
                        </>
                      ) : (
                        row.reviewer && <>&nbsp;by&nbsp;<strong>{row.reviewer.userName}</strong>&nbsp;<TimeAgo date={row.reviewDate} /></>
                      )}
                    </>
                  )}
                </>
              )}
            </ReviewStateContainer>
            <SheetRowOptions>
              {canFirstLevel && !isEditingComment && (
                <Button
                  secondary
                  type="button"
                  onClick={() => this.setState({editingCommentRowId: row.id})}>
                  <Icon name="message-circle" /> {row.overallComments ? 'Edit comment' : 'Add comment'}
                </Button>
              )}
              {user.role === 'ADMIN' && flags.isOn(user, 'SCALE_PRINT_ENABLED') && template.labelLayoutId && (
                <PrintToZebraControl sheetId={row.sheetId} rowId={row.id} credentials={credentials} />
              )}
              {user.role === 'ADMIN' && (
                <Button destructive onClick={() => this.handleDeleteRow(row)}><Icon name="trash-2" /> Delete Row</Button>
              )}
            </SheetRowOptions>
          </SheetRowFooterInfo>
        </Form>
      </SheetRowFooter>
    );
  }

  renderRowModal = ({location, match: {params: {templateId, sheetIds, id}}}) => {
    const {user} = this.props;
    let row, title, content;
    const isHistoryTab = location.pathname.indexOf('/history') >= 0;

    if (id) {
      const {rowsById} = this.state;
      row = rowsById[id];

      if (!row) {
        return null;
      }

      title = row.dates.lastModified ? new Date(row.dates.lastModified).toLocaleString() : '';
      content = (
        <Switch>
          <Route path="/sheet/:templateId/:sheetIds/data/:id/history" component={this.renderHistoryTab} />
          <Route path="/sheet/:templateId/:sheetIds/data/:id" component={this.renderAnswersTab} />
        </Switch>
      );
    }

    return (
      <SideModal
        large
        open={!!id}
        title={title}
        footer={!!id && this.renderRowFooter(row)}
        onClose={this.handleCloseRowDetails}
        tabs={user.role === Role.ADMIN && (
          <Tabs>
            <Tabs.Tab active={!isHistoryTab} to={`/sheet/${templateId}/${sheetIds}/data/${id}`}>Answers</Tabs.Tab>
            <Tabs.Tab active={isHistoryTab} to={`/sheet/${templateId}/${sheetIds}/data/${id}/history`}>History</Tabs.Tab>
          </Tabs>
        )}>
        {content}
      </SideModal>
    );
  }

  renderAnswersTab = ({match: {params: {id}}}) => {
    const {template, catalog} = this.props;
    const {sheetsById, rowsById} = this.state;
    const row = rowsById[id];
    const sheet = sheetsById[row.sheetId];

    return (
      <AnswersTab
        template={template}
        catalog={catalog}
        row={row}
        sheet={sheet}
        onAnswersChange={this.handleUpdateAnswers}
        showDeleted={this.props.showDeleted}
      />
    );
  }

  renderHistoryTab = ({match: {params: {id}}}) => {
    const {template, catalog} = this.props;
    const {rowsById, optimisticLogsByRow} = this.state;
    const row = rowsById[id];

    return (
      <SideModal.Content>
        <AuditLog
          tags={[`sheetRow.${row.id}`]}
          optimisticLogs={optimisticLogsByRow[row.id] || []}
          onLogsLoaded={logs => this.clearOptimisticCommentLogs(row.id, logs)}
          formatValue={(log, value) => {
            switch (log.action) {
              case 'review_state_changed':
              case 'row_reset_on_edit':
                return null;

              //an overall comment has no associated question, so just render the raw comment text
              //(shows as an "added value" block in the history tab)
              case 'overall_comment_changed':
                return value;

              default:
                const question = getQuestionFromLog(log, template);
                return <Answer question={question} answers={[{value}]} catalog={catalog} />;
            }
          }}>
          {log => {
            switch (log.action) {
              case 'review_state_changed': {
                const prev = log.previous[0];
                const next = log.next[0];
                const level = log.reviewLevel === 'SECOND' ? 'Second-level review' : 'First-level review';
                return (
                  <span>
                    {level} changed the status from&nbsp;
                    <ReviewState state={prev}>{formatReviewState(prev)}</ReviewState>
                    &nbsp;to&nbsp;
                    <ReviewState state={next}>{formatReviewState(next)}</ReviewState>
                  </span>
                );
              }

              case 'row_reset_on_edit':
                return <span>Row reset to Pending due to edit</span>;

              case 'overall_comment_changed':
                //previous is only populated when a prior comment existed, so it tells us add vs update
                return <span>{log.previous.length > 0 ? 'updated the comment on this row' : 'added a comment to this row'}</span>;

              default: {
                const question = getQuestionFromLog(log, template);
                return <span>changed the answer for <strong>{question.text}</strong></span>;
              }
            }
          }}
        </AuditLog>
      </SideModal.Content>
    );
  }

  renderCell = (cell, rowIndex, columnIndex, column, measure) => {
    const {template, catalog, user} = this.props;
    if (!cell) {
      // Return null or an empty cell for hidden data
      return null;
    }

    switch (cell.type) {
      case 'ALERTS':
        if (calculateAlerts(template, cell.row).length < 1) {
          return null;
        }

        return <Icon color="#FFB300" size="1.5em" name="alert-triangle" />;

      case 'REVIEW_STATUS':
        return <ReviewState fullWidth state={cell.row.reviewState}>{formatReviewState(cell.row.reviewState)}</ReviewState>;

      case 'DATE':
        const displayDate = new Date(cell.value);
        return (
          <DateCell>
            <span>{displayDate.toLocaleString()}</span>
            {cell.hasComment && (
              <HoverTooltip tooltip="This row has a comment">
                <CommentBadge>
                  <Icon name="message-circle" size="1em" />
                </CommentBadge>
              </HoverTooltip>
            )}
            {cell.isEdited && (
              <HoverTooltip tooltip="This row has been edited">
                <EditBadge>
                  <Icon name="edit-2" size="1em" />
                </EditBadge>
              </HoverTooltip>
            )}
          </DateCell>
        );

      case 'SHEET_LINK':
        return <LinkLookAlike>{cell.sheet.name || template.name}</LinkLookAlike>;

      case 'USER_LINK':
        return CanEditUser(user, cell.user) ? <LinkLookAlike>{cell.user.userName}</LinkLookAlike> : cell.user.userName;

      case 'ANSWER':
        if (!cell.answers || cell.answers.length < 1) {
          return null;
        }

        return (
          <AnswerCell question={cell.question}>
            <Answer question={cell.question} answers={cell.answers} catalog={catalog} onImageLoaded={measure} />
          </AnswerCell>
        );

      case 'VIEW_DETAILS':
        return <LinkLookAlike>View all answers...</LinkLookAlike>;

      default:
        throw new Error(`Unhandled cell type: ${cell.type}`);
    }
  }

  render() {
    const {loading, columns, data} = this.state;
    const {onBulkReview} = this.props; // set when the user is allowed to review this form's data
    const showDeleted = this.props.showDeleted; // show deleted questions

    if (loading) {
      return <Loading message="Loading responses..." />;
    }

    const filteredData = data.map(row => // filter out deleted questions if showDeleted is false
        row.filter(item =>
            !(
                item.type === "ANSWER" &&
                item.question &&
                isQuestionDeleted(item.question) &&
                !showDeleted
                // if it's an answer row, the data is of type question and the question is hidden without having show deleted toggled
            )
        )
    );
    // Build set of hidden questions
    const hiddenQuestionIds = new Set();
    data.forEach(row => {
      row.forEach(item => {
        if (item.type === "ANSWER" && item.question && isQuestionDeleted(item.question)) {
          hiddenQuestionIds.add(item.question.id);
        }
      });
    });

    const visibleColumns = columns.filter(col => {
      if (!showDeleted && hiddenQuestionIds.has(col.key)) return false; // remove hidden question columns
      return true; // keep all other columns
    });

    return (
      <Container>
        <Grid
          columns={visibleColumns}
          data={filteredData}
          children={this.renderCell}
          onCellClick={this.handleCellClick}
          titles={column => (
            <ColumnTitle>
              {column.key === 'review-status' && onBulkReview
                ? (
                  <ReviewColumnTitle>
                    <TitleText>{column.title || '\u00A0'}</TitleText>
                    <HoverTooltip tooltip="Approve All Pending Rows">
                      <Button tertiary onClick={() => onBulkReview('APPROVED')}><Icon name="check" /></Button>
                    </HoverTooltip>
                    <HoverTooltip tooltip="Reject All Pending Rows">
                      <RejectAllButton tertiary onClick={() => onBulkReview('REJECTED')}><Icon name="slash" /></RejectAllButton>
                    </HoverTooltip>
                  </ReviewColumnTitle>
                )
                : hiddenQuestionIds.has(column.key)
                ? (
                  <HoverTooltip tooltip="Question was deleted from a published form">
                    <DeletedBadge>
                      <TitleText>{column.title || '\u00A0'}</TitleText>
                      <Icon color="#D32F2F" size="1em" name="alert-triangle" />
                    </DeletedBadge>
                  </HoverTooltip>
                )
                : <TitleText>{column.title || '\u00A0'}</TitleText>
              }
            </ColumnTitle>
          )}
        />
        <Route path="/sheet/:templateId/:sheetIds/data/:id?" component={this.renderRowModal} />
      </Container>
    );
  }
}

function getQuestionFromLog(log, template) {
  return template.questions.filter(question => question.id === log.objectKey)[0];
}

//row-level "overall comment" editor shown in the row detail footer. Expanded from the "Add/Edit comment" button.
//It manages its own draft state, seeded from the row's saved comment. Keyed by row.id in the parent so switching
//rows re-seeds the draft
function RowCommentEditor({row, submitting, onSave, onCancel}) {
  const currentComment = row.overallComments || '';
  const [comment, setComment] = React.useState(currentComment);
  const trimmed = comment.trim();
  //only allow saving a non-empty comment that actually differs from what's already been saved
  const canSave = !submitting && trimmed !== '' && trimmed !== currentComment.trim();

  return (
    <OverallCommentContainer>
      <FooterFieldLabel>Overall comment</FooterFieldLabel>
      <ReviewCommentTextArea
        autoFocus
        disabled={submitting}
        placeholder="Add an overall comment about this row"
        value={comment}
        onChange={e => setComment(e.target.value)}
      />
      <OverallCommentActions>
        <Button secondary type="button" disabled={!canSave} onClick={() => onSave(trimmed)}>
          <Icon name="message-circle" /> <ButtonLabel>Save Comment</ButtonLabel>
        </Button>
        <Button secondary type="button" disabled={submitting} onClick={onCancel}>
          <Icon name="x-circle" /> <ButtonLabel>Cancel</ButtonLabel>
        </Button>
      </OverallCommentActions>
    </OverallCommentContainer>
  );
}

const Container = styled(Content)`
  margin-top: 1.875rem;
  height: calc(100vh - 14rem);
  min-height: 30rem;

  @media (max-width: 50rem) {
    margin-top: 1.25rem;
  }
`;

const Grid = styled(VirtualizedGrid)`
  height: 100%;
`;

const ColumnTitle = styled.h4`
  margin: 0;
  padding: 1rem 0 0 1rem;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TitleText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

// The bulk review actions sit in the status column header, so they have to fit in the height of a header cell.
const ReviewColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  margin-top: -0.5rem;

  ${Button} {
    padding: 0.25rem 0.375rem;
  }
`;

const RejectAllButton = styled(Button)`
  color: #B4131A;
`;

const DeletedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #D32F2F;
  text-decoration: line-through;
  flex-shrink: 0;

  svg {
    text-decoration: none;
  }
`;

const AnswerCell = styled.div`
  width: 100%;

  ${props => {
    if (props.question.type === 'NUMERIC' || props.question.type === 'MEASURED_NUMERIC') {
      return `text-align: right;`;
    }
  }}
`;

const LinkLookAlike = styled.span`
  color: #0049ee;
`;

const DateCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between; 
  gap: 0.5rem;
  width: 100%;

  > span:first-child {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
  }
`;

const EditBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e8edfa;
  color: #666b7e;
  width: 1.5em;
  height: 1.5em;
  border-radius: 50%;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0; /* keep constant size/alignment */

  svg {
    margin-top: -1px;
  }
`;

const CommentBadge = styled(EditBadge)`
  color: #0049ee;
`;

function formatReviewState(state) {
  if (state === 'IN_REVIEW') return 'IN REVIEW';
  return state;
}

const ReviewState = styled.div`
  display: inline-block;
  padding: 1px 1rem;
  text-align: center;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  background: ${props => {
    if (props.inverse) {
      switch (props.state) {
        case 'REJECTED': return '#a52824';
        case 'APPROVED': return '#3e8f58';
        case 'IN_REVIEW': return '#7c5cbf';
        case 'PENDING':
        default: return '#666b7e';
      }
    }

    switch (props.state) {
      case 'REJECTED': return '#fbedee';
      case 'APPROVED': return '#dff1e5';
      case 'IN_REVIEW': return '#f0ebfa';
      case 'PENDING':
      default: return '#e8edfa';
    }
  }};
  color: ${props => {
    if (props.inverse) {
      return '#ffffff';
    }

    switch (props.state) {
      case 'REJECTED': return '#a52824';
      case 'APPROVED': return '#006e21';
      case 'IN_REVIEW': return '#5a3d96';
      case 'PENDING':
      default: return '#666b7e';
    }
  }};

  ${props => {
    if (props.fullWidth) {
      return `width: 100%;`;
    }
  }}
`;

const SheetRowFooter = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 0.0625rem solid #e4e4e4;
  padding: 1.25rem;

  ${Button} {
    margin-right: 0.5rem;
  }
`;

const ReviewCommentContainer = styled.div`
  margin-bottom: 0.5rem;
`;

const OverallCommentContainer = styled.div`
  margin-bottom: 0.75rem;
`;

const FooterFieldLabel = styled.div`
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #747474;
`;

const SavedComment = styled.div`
  color: #666b7e;
  line-height: 1.4;
  //a comment can be up to 5000 characters, so make sure an unbroken run of text can't widen the footer
  overflow-wrap: anywhere;
`;

const OverallCommentActions = styled.div`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ButtonLabel = styled.span`
  position: relative;
  top: -0.0625rem;
`;

const SheetRowFooterInfo = styled.div`
  display: flex;
`;

const ReviewStateContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const SheetRowOptions = styled.div`
`;

const ReviewCommentTextArea = styled(TextArea)`
  display: block;
`;

export default withRouter(withUser(ViewDataTab));
